import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const SPEC_ICONS = [
  'ri-group-line',
  'ri-temp-cold-line',
  'ri-wifi-line',
  'ri-footprint-line',
  'ri-user-star-line',
  'ri-shield-check-line',
  'ri-luggage-cart-line',
  'ri-battery-charge-line',
];

export default function FleetSection() {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section
      id="fleet"
      ref={ref as React.RefObject<HTMLElement>}
      className={`w-full bg-[#1a1a1a] py-24 lg:py-32 overflow-hidden transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <p className="text-[#C9A84C] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
              {t('fleet_eyebrow')}
            </p>
            <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
              {t('fleet_h2_line1')}
              <span className="block text-[#C9A84C]">{t('fleet_h2_line2')}</span>
            </h2>
          </div>
          <p className="text-white/50 text-sm max-w-sm leading-relaxed">
            {t('fleet_subtitle')}
          </p>
        </div>

        {/* Main Fleet Content */}
        <div className="flex flex-col lg:flex-row gap-8 items-stretch">
          {/* Bus Image */}
          <div className="w-full lg:w-2/3 rounded-2xl overflow-hidden relative">
            <img
              src="https://storage.readdy-site.link/project_files/543a30f0-c088-40ca-b55f-d366a28be6b3/d2985777-8b28-4cd7-9836-d4b17b2f2180_Gemini_Generated_Image_w5d2y7w5d2y7w5d2.png?v=9ad2d5002ef6d9e1027fb7481fdbf107"
              alt="Phoenix Transportation luxury fleet"
              className="w-full h-72 md:h-96 lg:h-full object-cover object-center"
            />
          </div>

          {/* Specs Panel */}
          <div className="w-full lg:w-1/3 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 flex flex-col justify-between">
            <div>
              <h3 className="font-playfair text-xl font-bold text-white mb-2">
                {t('fleet_specs_title')}
              </h3>
              <div className="h-px bg-[#C9A84C]/40 mb-6" />

              <ul className="space-y-4">
                {SPEC_ICONS.map((icon, i) => (
                  <li key={i} className="flex items-center gap-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#C9A84C]/15 rounded-lg shrink-0">
                      <i className={`${icon} text-[#C9A84C] text-lg`} />
                    </div>
                    <span className="text-white/80 text-sm">{t(`fleet_spec_${i}`)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8">
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex items-center justify-center gap-2 w-full bg-[#C9A84C] hover:bg-[#b8953e] text-white font-semibold text-sm tracking-wider uppercase px-6 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                {t('fleet_bookSeat')} <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </div>

        {/* Route Banner */}
        <div className="mt-10 bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12">
            <div className="text-center">
              <p className="text-[#C9A84C] text-xs font-semibold tracking-widest uppercase mb-1">{t('fleet_route_departure_label')}</p>
              <p className="text-white text-xl font-bold font-playfair">Panama City</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-16 md:w-24 h-px bg-[#C9A84C]/50" />
              <i className="ri-bus-2-line text-[#C9A84C] text-2xl" />
              <div className="w-16 md:w-24 h-px bg-[#C9A84C]/50" />
            </div>
            <div className="text-center">
              <p className="text-[#C9A84C] text-xs font-semibold tracking-widest uppercase mb-1">{t('fleet_route_arrival_label')}</p>
              <p className="text-white text-xl font-bold font-playfair">Chitré</p>
            </div>
          </div>
          <div className="flex gap-8 text-center">
            <div>
              <p className="text-white/40 text-xs uppercase tracking-wide mb-1">{t('fleet_route_duration_label')}</p>
              <p className="text-white font-bold text-lg font-playfair">{t('fleet_route_duration_val')}</p>
            </div>
            <div className="w-px bg-white/10" />
            <div>
              <p className="text-white/40 text-xs uppercase tracking-wide mb-1">{t('fleet_route_trips_label')}</p>
              <p className="text-white font-bold text-lg font-playfair">{t('fleet_route_trips_val')}</p>
            </div>
            <div className="w-px bg-white/10" />
            <div>
              <p className="text-white/40 text-xs uppercase tracking-wide mb-1">{t('fleet_route_class_label')}</p>
              <p className="text-[#C9A84C] font-bold text-lg font-playfair">{t('fleet_route_class_val')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
